import React, { useState, createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { Box, CssBaseline } from '@mui/material';
import Header from './Header';
import Sidebar, { SidebarContext } from './Sidebar';
import { useLocation, Outlet } from 'react-router-dom';
import { Notification, NotificationType } from '../common';
import KeyboardShortcutsDialog from '../estimates/KeyboardShortcutsDialog';
import LiveCursorsOverlay from '../common/LiveCursorsOverlay';
import AiBusyOverlay from '../common/AiBusyOverlay';
import { useAuth } from '../../contexts/AuthContext';
import { useInternalChatNotifySound } from '../../hooks/useInternalChatNotifySound';

// Interfejs dla kontekstu powiadomień
interface NotificationContextType {
  showNotification: (message: string, type: NotificationType, linkUrl?: string, title?: string) => void;
}

// Tworzenie kontekstu powiadomień
export const NotificationContext = createContext<NotificationContextType>({
  showNotification: () => {},
});

// Hook do używania kontekstu powiadomień
export const useNotification = () => useContext(NotificationContext);

const Layout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  useInternalChatNotifySound(isAuthenticated, isLoading);
  const location = useLocation();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<NotificationType>('info');
  const [notificationLinkUrl, setNotificationLinkUrl] = useState<string | undefined>(undefined);
  const [notificationTitle, setNotificationTitle] = useState<string | undefined>(undefined);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(() => {
    try { return (localStorage.getItem('ui:sidebar:visible') ?? '0') === '1'; } catch { return false; }
  });
  const [aiBusy, setAiBusy] = useState(false);

  // Sprawdzanie rozmiaru ekranu do trybu responsywnego
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Nasłuchuj zdarzenia toggle sidebara (z karty Ustawienia → Widok)
  useEffect(() => {
    const onToggle = (e: any) => {
      if (e?.detail && typeof e.detail.visible === 'boolean') setSidebarVisible(e.detail.visible);
    };
    window.addEventListener('ui:sidebar:toggle', onToggle as any);
    return () => window.removeEventListener('ui:sidebar:toggle', onToggle as any);
  }, []);

  // Globalny wskaźnik AI busy (emitowany z AIDialog/AI importów)
  useEffect(() => {
    const onBusy = (e: any) => { setAiBusy(!!e?.detail?.busy); };
    const onReady = () => { setAiBusy(false); };
    window.addEventListener('ai:busy', onBusy as any);
    window.addEventListener('ai:ready', onReady as any);
    return () => {
      window.removeEventListener('ai:busy', onBusy as any);
      window.removeEventListener('ai:ready', onReady as any);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isEditable = !!(target && ((target as any).isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select'));
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      // macOS: Option + klawisz tyldy (fizyczny) → przełącz wysuwany sidebar (hamburger)
      // Bazuj na e.code, aby uniknąć konfliktu z Option+6 (który wpisuje '§').
      if (
        !isEditable &&
        isMac &&
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        (e.code === 'Backquote' || e.code === 'IntlBackslash' || e.code === 'Backslash')
      ) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('ui:overlay:toggle'));
        return;
      }

      // Windows: Alt + ` (Backquote) → przełącz wysuwany sidebar (hamburger)
      if (
        !isEditable &&
        !isMac &&
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        (e.code === 'Backquote' || e.key === '`')
      ) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('ui:overlay:toggle'));
        return;
      }

      // Okno skrótów Cmd/Ctrl + /
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      // Powiadomienia: klawisz "n" poza polami edycyjnymi
      if (!isEditable && !e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('ui:notifications:toggle'));
      }

      // Globalny fokus do searchbara: klawisz "/" (jak w Gmail)
      if (!isEditable && !e.ctrlKey && !e.metaKey && !e.altKey && e.key === '/') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('ui:search:focus'));
      }

      // Globalny skrót: klawisz "d" → wciśnij kontekstowy przycisk „Dodaj …"
      if (!isEditable && !e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === 'd') {
        // Jeśli czat jest otwarty, nie wykonuj akcji skrótu D
        const chatOpen = (() => { try { return !!(window as any).__unitrescoChatOpen; } catch { return false; } })();
        if (chatOpen) {
          return;
        }
        // Jeśli okno "Add offer by AI" jest otwarte, nie wykonuj akcji skrótu D
        const addOfferAIOpen = (() => { try { return !!(window as any).__unitrescoAddOfferAIOpen; } catch { return false; } })();
        if (addOfferAIOpen) {
          return;
        }
        const isVisible = (el: Element | null): boolean => {
          if (!el) return false;
          const htmlEl = el as HTMLElement;
          const style = window.getComputedStyle(htmlEl);
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
          const rect = htmlEl.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        };
        const isDisabled = (el: Element | null): boolean => {
          if (!el) return false;
          const htmlEl = el as any;
          return !!(htmlEl.disabled || htmlEl.getAttribute?.('aria-disabled') === 'true');
        };
        const tryClick = (el: Element | null): boolean => {
          if (!el) return false;
          if (!isVisible(el) || isDisabled(el)) return false;
          (el as HTMLElement).click();
          return true;
        };
        e.preventDefault();
        // 1) Wyraźnie oznaczone cele
        const explicit = document.querySelector('[data-hotkey-add="true"], [data-hotkey="add"], [data-role="add-primary"]');
        if (tryClick(explicit)) return;
        // 2) Atrybuty data-action
        const dataAction = document.querySelector('[data-action="add"], [data-action^="add-"]');
        if (tryClick(dataAction)) return;
        // 3) aria-label lub title zawiera „Dodaj”
        const labelled = Array.from(document.querySelectorAll('[aria-label], [title]')).find((el) => {
          const a = (el.getAttribute('aria-label') || '').toLowerCase();
          const t = (el.getAttribute('title') || '').toLowerCase();
          return (a.startsWith('dodaj') || t.startsWith('dodaj')) && isVisible(el) && !isDisabled(el);
        });
        if (tryClick(labelled || null)) return;
        // 4) Guziki z tekstem zaczynającym się od „Dodaj”
        const candidates = Array.from(document.querySelectorAll('button, [role="button"], a.MuiButton-root'));
        for (const el of candidates) {
          const text = (el.textContent || '').trim().toLowerCase();
          if (text.startsWith('dodaj')) {
            if (tryClick(el)) return;
          }
        }
      }
    };
    const handleOpenShortcuts = () => setShortcutsOpen(true);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('ui:shortcuts:open', handleOpenShortcuts as any);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('ui:shortcuts:open', handleOpenShortcuts as any);
    };
  }, []);

  // Funkcja do wyświetlania powiadomień (stabilna referencja — unikamy ponownych efektów w konsumentach, np. EasyEstimate).
  const showNotification = useCallback((message: string, type: NotificationType, linkUrl?: string, title?: string) => {
    const getDefaultTitle = (t: NotificationType): string => {
      switch (t) {
        case 'success':
          return 'Sukces';
        case 'warning':
          return 'Ostrzeżenie';
        case 'error':
          return 'Błąd';
        case 'info':
          return 'Info';
        default:
          return 'Info';
      }
    };

    setNotificationMessage(message);
    setNotificationType(type);
    setNotificationLinkUrl(linkUrl);
    setNotificationTitle(title !== undefined ? title : getDefaultTitle(type));
    setNotificationOpen(true);
  }, []);

  // Funkcja do zamykania powiadomień
  const handleCloseNotification = () => {
    setNotificationOpen(false);
  };

  const isBoardRoute = location.pathname.startsWith('/board');
  /** Pełna wysokość pod tabelę (sticky); wewnętrzne odstępy ma sam widok. */
  const isEasyEstimateEstimateDetail = location.pathname.startsWith('/app/easy-estimate/estimate/');
  /** Strona agregująca pozycje zweryfikowane — też pełna wysokość, ale bez dodatkowego +25 px marginesu. */
  const isEasyEstimateAllVerified = location.pathname === '/app/easy-estimate/zweryfikowane';
  const isEasyEstimateFullHeight = isEasyEstimateEstimateDetail || isEasyEstimateAllVerified;

  const notificationContextValue = useMemo(() => ({ showNotification }), [showNotification]);

  return (
    <NotificationContext.Provider value={notificationContextValue}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <CssBaseline />
        <Sidebar />
        <Header setShortcutsOpen={setShortcutsOpen} />
        <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        <Notification
          open={notificationOpen}
          message={notificationMessage}
          type={notificationType}
          onClose={handleCloseNotification}
          linkUrl={notificationLinkUrl}
          title={notificationTitle}
        />
        <LiveCursorsOverlay />
      <Box 
        component="main" 
        sx={{ 
          marginLeft: (!isMobile && sidebarVisible) ? 'calc(40px + 12px)' : '12px',
          width: (!isMobile && sidebarVisible) ? 'calc(100% - 40px - 12px)' : 'calc(100% - 12px)',
          p: isBoardRoute || isEasyEstimateFullHeight ? 0 : 3,
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1, // Niższy z-index niż sidebar
          flex: '1 0 auto',
          backgroundColor: (theme) => theme.palette.background.default,
          color: (theme) => theme.palette.text.primary,
          minHeight: 'calc(100vh - 52px + 10px - var(--legacy-host-banner-height, 0px))',
          display: 'flex',
          flexDirection: 'column',
          mt: isEasyEstimateEstimateDetail
            ? 'calc(52px - 10px + 25px + var(--legacy-host-banner-height, 0px))' // +25px — odstęp od topbara (15+10)
            : 'calc(52px - 10px + var(--legacy-host-banner-height, 0px))', // Header (52px) – podniesienie o 10px
          ...(isBoardRoute
            ? { overflow: 'hidden', height: 'calc(100vh - 52px - var(--legacy-host-banner-height, 0px))', maxHeight: 'calc(100vh - 52px - var(--legacy-host-banner-height, 0px))' }
            : isEasyEstimateEstimateDetail
              ? {
                  overflow: 'hidden',
                  minHeight: 'calc(100vh - 52px + 10px - 25px - var(--legacy-host-banner-height, 0px))',
                  height: 'calc(100vh - 52px + 10px - 25px - var(--legacy-host-banner-height, 0px))',
                  maxHeight: 'calc(100vh - 52px + 10px - 25px - var(--legacy-host-banner-height, 0px))',
                }
              : isEasyEstimateAllVerified
                ? {
                    overflow: 'hidden',
                    minHeight: 'calc(100vh - 52px + 10px - var(--legacy-host-banner-height, 0px))',
                    height: 'calc(100vh - 52px + 10px - var(--legacy-host-banner-height, 0px))',
                    maxHeight: 'calc(100vh - 52px + 10px - var(--legacy-host-banner-height, 0px))',
                  }
                : {}),
        }}
          // Tryb obserwacji: blokuj interakcje admina, gdy śledzi kogoś (overlay kursorów ma pointerEvents none)
          onMouseDown={(e) => { try { const uid = localStorage.getItem('cobrowse:follow:userId'); if (uid) { if ((e.target as HTMLElement)?.closest?.('#cobrowse-exit')) return; e.preventDefault(); e.stopPropagation(); } } catch {} }}
          onClick={(e) => { try { const uid = localStorage.getItem('cobrowse:follow:userId'); if (uid) { if ((e.target as HTMLElement)?.closest?.('#cobrowse-exit')) return; e.preventDefault(); e.stopPropagation(); } } catch {} }}
          onChange={(e) => { try { const uid = localStorage.getItem('cobrowse:follow:userId'); if (uid) { e.preventDefault(); e.stopPropagation(); } } catch {} }}
        >
          <Outlet />
          <AiBusyOverlay busy={aiBusy} />
        </Box>
      </Box>
    </NotificationContext.Provider>
  );
};

export default Layout;
